import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { MessageSquare, Search, Send } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

export default function Messages() {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConvs(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  const filtered = q.trim()
    ? convs.filter((c) =>
        (c.other?.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (c.last_message?.content || "").toLowerCase().includes(q.toLowerCase())
      )
    : convs;

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="overline text-burgundy flex items-center gap-2">
            <MessageSquare className="h-3.5 w-3.5" /> Messages
          </div>
          <h1 className="font-serif text-4xl text-jjblue mt-2 leading-tight">Your conversations</h1>
        </div>
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--jj-muted)]" />
        <input
          className="input-jj pl-10"
          placeholder="Search a member or a message…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="messages-search"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center text-[var(--jj-muted)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="jj-card p-10 text-center" data-testid="messages-empty">
          <div className="h-12 w-12 rounded-full bg-cream-surface flex items-center justify-center mx-auto">
            <Send className="h-5 w-5 text-burgundy" strokeWidth={1.5} />
          </div>
          <h3 className="font-serif text-2xl text-jjblue mt-4">{q ? "No conversations match." : "Start a conversation."}</h3>
          <p className="mt-2 text-sm text-[var(--jj-text-secondary)] max-w-md mx-auto">
            {q
              ? "Try a different search."
              : "When you message someone — or they message you — your conversations land here."}
          </p>
          {!q && (
            <Link to="/cinema" className="btn-primary !py-2 !px-5 text-sm mt-6 inline-block" data-testid="messages-go-cinema">
              Browse Cinema to find a watch-party host
            </Link>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((c) => (
            <li key={c.id}>
              <Link
                to={`/messages/${c.other.id}`}
                className={`flex items-center gap-4 jj-card p-4 transition-colors ${
                  c.unread_count > 0 ? "border-gold bg-gold/[0.06]" : "hover:border-gold"
                }`}
                data-testid={`conv-${c.id}`}
              >
                <Avatar user={c.other} size={44} testId={`conv-avatar-${c.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`font-medium truncate ${c.unread_count > 0 ? "text-jjblue" : "text-jjblue/85"}`}>{c.other.name}</div>
                    <span className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)]">{c.other.tier}</span>
                  </div>
                  {c.last_message?.content && (
                    <div className="mt-0.5 text-sm text-[var(--jj-text-secondary)] truncate">{c.last_message.content}</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[11px] text-[var(--jj-muted)]">{timeAgo(c.last_message?.created_at)}</span>
                  {c.unread_count > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-burgundy text-cream text-[10px] font-semibold flex items-center justify-center" data-testid={`conv-unread-${c.id}`}>
                      {c.unread_count > 9 ? "9+" : c.unread_count}
                    </span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
